const MAGIC = [0x50, 0x4C, 0x53, 0x52]; // "PLSR"
const HEADER_SIZE = 32;
const ROSTER_ENTRY_SIZE = 10;
const PLAYER_FRAME_SIZE = 26;

const PACKET_FRAME = 0x00;
const PACKET_KILL = 0x01;
const PACKET_SHOT = 0x02;
const PACKET_HE_DETONATE = 0x03;
const PACKET_FLASH_DETONATE = 0x04;
const PACKET_SMOKE_START = 0x05;
const PACKET_SMOKE_EXPIRE = 0x06;
const PACKET_INFERNO_START = 0x07;
const PACKET_INFERNO_EXPIRE = 0x08;
const PACKET_DECOY_START = 0x09;
const PACKET_DECOY_EXPIRE = 0x0a;
const PACKET_BOMB_PLANT = 0x0b;
const PACKET_BOMB_BEGIN_DEFUSE = 0x0c;
const PACKET_BOMB_DEFUSED = 0x0d;
const PACKET_BOMB_EXPLODED = 0x0e;
const PACKET_PLAYER_HURT = 0x0f;
const PACKET_FLASH_BLIND = 0x10;
const PACKET_BULLET_IMPACT = 0x11;
const PACKET_DEFUSER_DROP = 0x12;
const PACKET_DEFUSER_PICKUP = 0x13;
const PACKET_SMOKE_THROW = 0x14;

export const WEAPON_NAMES: Record<number, string> = {
    0: 'Unknown', 1: 'Glock-18', 2: 'USP-S', 3: 'P2000', 4: 'P250',
    5: 'Five-SeveN', 6: 'Tec-9', 7: 'CZ75-Auto', 8: 'Desert Eagle',
    9: 'R8 Revolver', 10: 'Dual Berettas', 11: 'MAC-10', 12: 'MP9',
    13: 'MP7', 14: 'MP5-SD', 15: 'UMP-45', 16: 'P90', 17: 'PP-Bizon',
    20: 'Galil AR', 21: 'FAMAS', 22: 'AK-47', 23: 'M4A4', 24: 'M4A1-S',
    25: 'SG 553', 26: 'AUG', 30: 'SSG 08', 31: 'AWP', 32: 'SCAR-20',
    33: 'G3SG1', 40: 'Nova', 41: 'XM1014', 42: 'Sawed-Off', 43: 'MAG-7',
    50: 'Negev', 51: 'M249', 60: 'Knife', 61: 'Zeus x27',
    70: 'HE Grenade', 71: 'Flashbang', 72: 'Smoke Grenade',
    73: 'Molotov', 74: 'Incendiary', 75: 'Decoy', 80: 'C4',
};

const FLAG_NAMES = [
    'alive', 'scoped', 'defusing', 'planting', 'hasHelmet', 'hasBomb',
    'hasDefuse', 'isDucking', 'isBlinded', 'isAirborne', 'isReloading',
    'isWalking', 'inBuyZone', 'inBombZone', 'isStanding',
];

export interface ReplayHeader {
    version: number;
    playerCount: number;
    packetCount: number;
    tickRate: number;
    sampleRate: number;
    roundNumber: number;
    mapName: string;
}

export interface RosterEntry {
    steamId: string;
    slot: number;
    team: 'T' | 'CT';
}

export interface PlayerFrame {
    x: number;
    y: number;
    z: number;
    yaw: number;
    pitch: number;
    hp: number;
    armor: number;
    weaponId: number;
    flags: number;
    flashRemain: number;
    money: number;
    ammoClip: number;
    ammoReserve: number;
    equipValue: number;
    utilityFlash: number;
    utilitySmoke: number;
    utilityHE: number;
    utilityFire: number;
    zoomLevel: number;
    ping: number;
    velX: number;
    velY: number;
    isAlive: boolean;
}

export interface Projectile {
    type: number;
    x: number;
    y: number;
    throwerSlot: number;
}

export interface ActiveSmoke {
    entityId: number;
    x: number;
    y: number;
    startTick: number;
}

export interface SmokeBreak {
    smokeEntityId: number;
    frameIdx: number;
    x: number;
    y: number;
}

export interface FlamePoint {
    x: number;
    y: number;
}

export interface ActiveFire {
    uniqueId: number;
    x: number;
    y: number;
    flames: FlamePoint[];
}

export interface DroppedDefuseKit {
    entityId: number;
    x: number;
    y: number;
}

export interface DroppedItem {
    entityId: number;
    itemId: number;   // WeaponIdMapper compact ID — same space as PlayerFrame.weaponId
    x: number;
    y: number;
    z?: number;       // v4+; undefined on older replays
}

export interface WorldState {
    bombX: number;
    bombY: number;
    projectiles: Projectile[];
    smokes: ActiveSmoke[];
    fires: ActiveFire[];
    droppedKits: DroppedDefuseKit[];
    droppedItems: DroppedItem[]; // v3+, empty array on older replays
}

export interface Frame {
    tick: number;
    players: PlayerFrame[];
    world: WorldState;
}

export interface KillEvent {
    type: 'kill';
    frameIdx: number;
    killerSlot: number;
    victimSlot: number;
    weaponId: number;
    headshot: boolean;
    wallbang: boolean;
    noscope: boolean;
    thrusmoke: boolean;
    assisterSlot: number | null;
    distance: number;
}

export interface ShotEvent {
    type: 'shot';
    frameIdx: number;
    shooterSlot: number;
    weaponId: number;
    x: number;
    y: number;
    zoomLevel: number;
    ammoAfter: number;
}

export interface GrenadeDetonateEvent {
    type: 'he_detonate' | 'flash_detonate';
    frameIdx: number;
    throwerSlot: number;
    x: number;
    y: number;
}

export interface EntityStartEvent {
    type: 'smoke_start' | 'inferno_start' | 'decoy_start';
    frameIdx: number;
    throwerSlot: number;
    x: number;
    y: number;
    entityId: number;
}

export interface EntityExpireEvent {
    type: 'smoke_expire' | 'inferno_expire' | 'decoy_expire';
    frameIdx: number;
    entityId: number;
}

export interface BombPlantEvent {
    type: 'bomb_plant';
    frameIdx: number;
    planterSlot: number;
    x: number;
    y: number;
}

export interface BombDefuseStartEvent {
    type: 'bomb_begin_defuse';
    frameIdx: number;
    defuserSlot: number;
    hasKit: boolean;
}

export interface BombEndEvent {
    type: 'bomb_defused' | 'bomb_exploded';
    frameIdx: number;
}

export interface PlayerHurtEvent {
    type: 'player_hurt';
    frameIdx: number;
    attackerSlot: number | null;
    victimSlot: number;
    hpDamage: number;
    armorDamage: number;
    weaponId: number;
    hitgroup: number;
    friendlyFire: boolean;
}

export interface FlashBlindEvent {
    type: 'flash_blind';
    frameIdx: number;
    attackerSlot: number | null;
    victimSlot: number;
    durationSeconds: number;
}

export interface BulletImpactEvent {
    type: 'bullet_impact';
    frameIdx: number;
    shooterSlot: number;
    shooterX: number;
    shooterY: number;
    impactX: number;
    impactY: number;
}

export interface DefuserDropEvent {
    type: 'defuser_drop';
    frameIdx: number;
    entityId: number;
    x: number;
    y: number;
}

export interface DefuserPickupEvent {
    type: 'defuser_pickup';
    frameIdx: number;
    entityId: number;
}

export interface SmokeThrowEvent {
    type: 'smoke_throw';
    frameIdx: number;
    throwerSlot: number;
    posX: number;
    posY: number;
    posZ: number;
    yaw: number;
    pitch: number;
    velX: number;
    velY: number;
    velZ: number;
    inputButtons: number;       // lower 32 bits of the bitmask (sufficient for all known flags)
    absoluteTick: number;       // absolute demo tick for clip system seeking
    // Decoded convenience fields from InputButtons bitmask
    isAttack: boolean;          // bit 0 - left click (full throw)
    isJumping: boolean;         // bit 1
    isCrouched: boolean;        // bit 2
    isForward: boolean;         // bit 3 (W)
    isBack: boolean;            // bit 4 (S)
    isMoveLeft: boolean;        // bit 9 (A)
    isMoveRight: boolean;       // bit 10 (D)
    isAttackSecondary: boolean; // bit 11 - right click (short throw)
    isWalking: boolean;         // bit 16 (shift)
    throwStrength: 'Full' | 'Short' | 'Medium' | 'Unknown';
    movementState: 'Standing' | 'Walking' | 'Running';
}

export type ReplayEvent =
    | KillEvent | ShotEvent | GrenadeDetonateEvent | EntityStartEvent
    | EntityExpireEvent | BombPlantEvent | BombDefuseStartEvent
    | BombEndEvent | PlayerHurtEvent | FlashBlindEvent | BulletImpactEvent
    | DefuserDropEvent | DefuserPickupEvent | SmokeThrowEvent;

export interface ParsedReplay {
    header: ReplayHeader;
    roster: RosterEntry[];
    frames: Frame[];
    events: ReplayEvent[];
    smokeThrows: SmokeThrowEvent[];
}

class BinaryReader {
    private view: DataView;
    private offset: number;

    constructor(buffer: ArrayBuffer) {
        this.view = new DataView(buffer);
        this.offset = 0;
    }

    u8(): number { return this.view.getUint8(this.offset++); }
    i8(): number { return this.view.getInt8(this.offset++); }
    u16(): number { const v = this.view.getUint16(this.offset, false); this.offset += 2; return v; }
    i16(): number { const v = this.view.getInt16(this.offset, false); this.offset += 2; return v; }
    u64(): bigint { const v = this.view.getBigUint64(this.offset, false); this.offset += 8; return v; }
    bytes(n: number): Uint8Array { const v = new Uint8Array(this.view.buffer, this.offset, n); this.offset += n; return v; }
    ascii(n: number): string { return String.fromCharCode(...Array.from(this.bytes(n))); }
    get remaining(): number { return this.view.byteLength - this.offset; }
}

function parsePlayerFrame(r: BinaryReader): PlayerFrame {
    const x = r.i16();
    const y = r.i16();
    const z = r.i16();
    const yawRaw = r.u8();
    const pitchRaw = r.i8();
    const hp = r.u8();
    const armor = r.u8();
    const weaponId = r.u8();
    const flags = r.u16();
    const flashRemain = r.u8();
    const money = r.u16();
    const ammoClip = r.u8();
    const ammoReserve = r.u8();
    const equipValue = r.u16();
    const utilPacked = r.u8();
    const miscPacked = r.u8();
    const velX = r.i16();
    const velY = r.i16();

    return {
        x, y, z,
        yaw: (yawRaw / 255) * 360,
        pitch: (pitchRaw / 127) * 90,
        hp, armor, weaponId, flags,
        flashRemain: flashRemain / 10,
        money, ammoClip, ammoReserve, equipValue,
        utilityFlash: (utilPacked >> 6) & 0x03,
        utilitySmoke: (utilPacked >> 4) & 0x03,
        utilityHE: (utilPacked >> 2) & 0x03,
        utilityFire: utilPacked & 0x03,
        zoomLevel: (miscPacked >> 6) & 0x03,
        ping: (miscPacked & 0x3f) * 4,
        velX, velY,
        isAlive: (flags & 1) !== 0,
    };
}

function parseWorldState(r: BinaryReader, version: number): WorldState {
    const bombX = r.i16();
    const bombY = r.i16();

    const projCount = r.u8();
    const projectiles: Projectile[] = [];
    for (let i = 0; i < projCount; i++) {
        projectiles.push({ type: r.u8(), x: r.i16(), y: r.i16(), throwerSlot: r.u8() });
    }

    const smokeCount = r.u8();
    const smokes: ActiveSmoke[] = [];
    for (let i = 0; i < smokeCount; i++) {
        smokes.push({ entityId: r.u16(), x: r.i16(), y: r.i16(), startTick: r.u16() });
    }

    const fireCount = r.u8();
    const fires: ActiveFire[] = [];
    if (version >= 2) {
        for (let i = 0; i < fireCount; i++) {
            const uniqueId = r.u16();
            const flameCount = r.u8();
            const flames: FlamePoint[] = [];
            for (let j = 0; j < flameCount; j++) {
                flames.push({ x: r.i16(), y: r.i16() });
            }
            let cx = 0, cy = 0;
            if (flames.length > 0) {
                for (const f of flames) { cx += f.x; cy += f.y; }
                cx /= flames.length;
                cy /= flames.length;
            }
            fires.push({ uniqueId, x: cx, y: cy, flames });
        }
    } else {
        for (let i = 0; i < fireCount; i++) {
            fires.push({ uniqueId: r.u16(), x: r.i16(), y: r.i16(), flames: [] });
        }
    }

    const kitCount = r.u8();
    const droppedKits: DroppedDefuseKit[] = [];
    for (let i = 0; i < kitCount; i++) {
        droppedKits.push({ entityId: r.u16(), x: r.i16(), y: r.i16() });
    }

    const droppedItems: DroppedItem[] = [];
    if (version >= 4) {
        const itemCount = r.u8();
        for (let i = 0; i < itemCount; i++) {
            droppedItems.push({ entityId: r.u16(), itemId: r.u8(), x: r.i16(), y: r.i16(), z: r.i16() });
        }
    } else if (version >= 3) {
        const itemCount = r.u8();
        for (let i = 0; i < itemCount; i++) {
            droppedItems.push({ entityId: r.u16(), itemId: r.u8(), x: r.i16(), y: r.i16() });
        }
    }

    return { bombX, bombY, projectiles, smokes, fires, droppedKits, droppedItems };
}

function parseEvent(r: BinaryReader, ptype: number): ReplayEvent {
    const frameIdx = r.u16();

    switch (ptype) {
        case PACKET_KILL: {
            const killerSlot = r.u8();
            const victimSlot = r.u8();
            const weaponId = r.u8();
            const flags = r.u8();
            const assister = r.u8();
            const distance = r.u16() / 10;
            return {
                type: 'kill', frameIdx, killerSlot, victimSlot, weaponId,
                headshot: (flags & 1) !== 0, wallbang: (flags & 2) !== 0,
                noscope: (flags & 4) !== 0, thrusmoke: (flags & 8) !== 0,
                assisterSlot: assister === 0xff ? null : assister, distance,
            };
        }
        case PACKET_SHOT:
            return {
                type: 'shot', frameIdx, shooterSlot: r.u8(), weaponId: r.u8(),
                x: r.i16(), y: r.i16(), zoomLevel: r.u8(), ammoAfter: r.u8(),
            };
        case PACKET_HE_DETONATE:
        case PACKET_FLASH_DETONATE:
            return {
                type: ptype === PACKET_HE_DETONATE ? 'he_detonate' : 'flash_detonate',
                frameIdx, throwerSlot: r.u8(), x: r.i16(), y: r.i16(),
            };
        case PACKET_SMOKE_START:
        case PACKET_INFERNO_START:
        case PACKET_DECOY_START: {
            const typeMap = { [PACKET_SMOKE_START]: 'smoke_start', [PACKET_INFERNO_START]: 'inferno_start', [PACKET_DECOY_START]: 'decoy_start' } as const;
            return {
                type: typeMap[ptype], frameIdx, throwerSlot: r.u8(),
                x: r.i16(), y: r.i16(), entityId: r.u16(),
            };
        }
        case PACKET_SMOKE_EXPIRE:
        case PACKET_INFERNO_EXPIRE:
        case PACKET_DECOY_EXPIRE: {
            const typeMap = { [PACKET_SMOKE_EXPIRE]: 'smoke_expire', [PACKET_INFERNO_EXPIRE]: 'inferno_expire', [PACKET_DECOY_EXPIRE]: 'decoy_expire' } as const;
            return { type: typeMap[ptype], frameIdx, entityId: r.u16() };
        }
        case PACKET_BOMB_PLANT:
            return { type: 'bomb_plant', frameIdx, planterSlot: r.u8(), x: r.i16(), y: r.i16() };
        case PACKET_BOMB_BEGIN_DEFUSE:
            return { type: 'bomb_begin_defuse', frameIdx, defuserSlot: r.u8(), hasKit: r.u8() !== 0 };
        case PACKET_BOMB_DEFUSED:
            return { type: 'bomb_defused', frameIdx };
        case PACKET_BOMB_EXPLODED:
            return { type: 'bomb_exploded', frameIdx };
        case PACKET_PLAYER_HURT: {
            const attackerSlot = r.u8();
            return {
                type: 'player_hurt', frameIdx,
                attackerSlot: attackerSlot === 0xff ? null : attackerSlot,
                victimSlot: r.u8(), hpDamage: r.u8(), armorDamage: r.u8(),
                weaponId: r.u8(), hitgroup: r.u8(), friendlyFire: r.u8() !== 0,
            };
        }
        case PACKET_FLASH_BLIND: {
            const attackerSlot = r.u8();
            return {
                type: 'flash_blind', frameIdx,
                attackerSlot: attackerSlot === 0xff ? null : attackerSlot,
                victimSlot: r.u8(), durationSeconds: r.u16() / 100,
            };
        }
        case PACKET_BULLET_IMPACT:
            return {
                type: 'bullet_impact', frameIdx,
                shooterSlot: r.u8(),
                shooterX: r.i16(), shooterY: r.i16(),
                impactX: r.i16(), impactY: r.i16(),
            };
        case PACKET_DEFUSER_DROP:
            return {
                type: 'defuser_drop', frameIdx,
                entityId: r.u16(), x: r.i16(), y: r.i16(),
            };
        case PACKET_DEFUSER_PICKUP:
            return {
                type: 'defuser_pickup', frameIdx,
                entityId: r.u16(),
            };
        case PACKET_SMOKE_THROW: {
            const throwerSlot = r.u8();
            const posX = r.i16();
            const posY = r.i16();
            const posZ = r.i16();
            const yaw = r.i16() / 100;
            const pitch = r.i16() / 100;
            const velX = r.i16();
            const velY = r.i16();
            const velZ = r.i16();
            // Read 8-byte uint64, convert lower 32 bits to Number (sufficient for all known input flags up to bit 16)
            const buttonsRaw = r.u64();
            const inputButtons = Number(buttonsRaw & BigInt(0xFFFFFFFF));
            // Absolute game tick (uint32) for clip system seeking
            const absoluteTick = r.u16() * 65536 + r.u16(); // read as two u16 to form u32
            r.bytes(2); // reserved
            const isAttack = (inputButtons & (1 << 0)) !== 0;
            const isJumpButton = (inputButtons & (1 << 1)) !== 0;
            // Jump throws: button is often released before grenade fires, but player is airborne (velZ != 0)
            const isJumping = isJumpButton || Math.abs(velZ) > 10;
            const isCrouched = (inputButtons & (1 << 2)) !== 0;
            const isForward = (inputButtons & (1 << 3)) !== 0;
            const isBack = (inputButtons & (1 << 4)) !== 0;
            const isMoveLeft = (inputButtons & (1 << 9)) !== 0;
            const isMoveRight = (inputButtons & (1 << 10)) !== 0;
            const isAttackSecondary = (inputButtons & (1 << 11)) !== 0;
            const isWalking = (inputButtons & (1 << 16)) !== 0;
            const throwStrength: SmokeThrowEvent['throwStrength'] =
                isAttack && isAttackSecondary ? 'Medium' :
                isAttack ? 'Full' :
                isAttackSecondary ? 'Short' : 'Unknown';
            const speed = Math.sqrt(velX * velX + velY * velY);
            const movementState: SmokeThrowEvent['movementState'] =
                speed < 5 ? 'Standing' :
                isWalking || speed < 85 ? 'Walking' : 'Running';
            return {
                type: 'smoke_throw', frameIdx, throwerSlot,
                posX, posY, posZ, yaw, pitch, velX, velY, velZ,
                inputButtons, absoluteTick, isAttack, isJumping, isCrouched,
                isForward, isBack, isMoveLeft, isMoveRight,
                isAttackSecondary, isWalking, throwStrength, movementState,
            };
        }
        default:
            throw new Error(`Unknown packet type: 0x${ptype.toString(16)}`);
    }
}

export function parseReplay(buffer: ArrayBuffer): ParsedReplay {
    const r = new BinaryReader(buffer);

    // Header
    const magic = r.bytes(4);
    if (magic[0] !== MAGIC[0] || magic[1] !== MAGIC[1] || magic[2] !== MAGIC[2] || magic[3] !== MAGIC[3]) {
        throw new Error('Invalid replay file: bad magic bytes');
    }

    const version = r.u8();
    const playerCount = r.u8();
    const packetCount = r.u16();
    const tickRate = r.u8();
    const sampleRate = r.u8();
    const roundNumber = r.u8();
    const mapNameLen = r.u8();
    const mapName = r.ascii(mapNameLen);
    // Skip padding to 32 bytes
    r.bytes(HEADER_SIZE - 12 - mapNameLen);

    const header: ReplayHeader = { version, playerCount, packetCount, tickRate, sampleRate, roundNumber, mapName };

    // Roster
    const roster: RosterEntry[] = [];
    for (let i = 0; i < playerCount; i++) {
        const steamId = r.u64().toString();
        const slot = r.u8();
        const team = r.u8() === 0 ? 'T' : 'CT';
        roster.push({ steamId, slot, team } as RosterEntry);
    }

    // Packet stream
    const frames: Frame[] = [];
    const events: ReplayEvent[] = [];
    const smokeThrows: SmokeThrowEvent[] = [];
    let packetsRead = 0;

    while (r.remaining > 0 && packetsRead < packetCount) {
        const ptype = r.u8();
        packetsRead++;

        if (ptype === PACKET_FRAME) {
            const tick = r.u16();
            const players: PlayerFrame[] = [];
            for (let i = 0; i < playerCount; i++) {
                players.push(parsePlayerFrame(r));
            }
            const world = parseWorldState(r, version);
            frames.push({ tick, players, world });
        } else {
            const event = parseEvent(r, ptype);
            events.push(event);
            if (event.type === 'smoke_throw') {
                smokeThrows.push(event);
            }
        }
    }

    return { header, roster, frames, events, smokeThrows };
}

export function getActiveFlags(flags: number): string[] {
    const active: string[] = [];
    for (let i = 0; i < FLAG_NAMES.length; i++) {
        if (flags & (1 << i)) active.push(FLAG_NAMES[i]);
    }
    return active;
}

export function getDurationSeconds(replay: ParsedReplay): number {
    if (replay.frames.length === 0) return 0;
    const lastTick = replay.frames[replay.frames.length - 1].tick;
    return lastTick / Math.max(replay.header.tickRate, 1);
}

// Index of the last frame at or before `tick`, clamped to the round. Seeking by tick rather than
// by frame count keeps "5 seconds" correct whatever the frame cadence is.
export function frameIndexAtTick(frames: Frame[], tick: number): number {
    let lo = 0;
    let hi = frames.length - 1;
    if (hi < 0) return 0;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (frames[mid].tick <= tick) lo = mid;
        else hi = mid - 1;
    }
    return lo;
}
